import React from 'react';
import Button from '../Button';
import CaretDownIcon from '../CustomIcons/CaretDownIcon';
import Typography from '../Typography';
import { useStickToBottom } from './stickToBottom';

export interface ChatThreadProps {
    children?: React.ReactNode;
    /** Names the transcript for assistive tech, e.g. "Policy assistant conversation". */
    'aria-label'?: string;
    /** Shown, centred, while the transcript holds nothing. Ignored when `emptyContent` is
     *  supplied. */
    emptyTitle?: string;
    /** The lines under the title. A node, because guidance is rarely one sentence. Ignored
     *  when `emptyContent` is supplied. */
    emptyDescription?: React.ReactNode;
    /** Ignored when `emptyContent` is supplied. */
    emptyIcon?: React.ReactNode;
    /**
     * Replaces the `emptyTitle`/`emptyDescription`/`emptyIcon` composition. Renders
     * left-aligned at the bottom of the transcript, directly above the composer — an empty
     * conversation should read as ready to start, not as a decorated void.
     */
    emptyContent?: React.ReactNode;
    className?: string;
}

/**
 * The scrolling transcript. Owns scroll ownership and the pin-to-newest behaviour so a
 * consumer never wires a scroll listener of its own.
 *
 * `flex-1 min-h-0` is load-bearing: without the floor, WebKit shrinks an `overflow` child to
 * fit its content and scrolling silently moves to an ancestor.
 */
const ChatThread: React.FC<ChatThreadProps> = ({
    children,
    'aria-label': ariaLabel,
    emptyTitle,
    emptyDescription,
    emptyIcon,
    emptyContent,
    className = '',
}) => {
    const { scrollRef, contentRef, pinned, scrollToBottom } = useStickToBottom();

    // `toArray` rather than `count`: it drops the nulls a caller leaves behind when a
    // conditional turn renders nothing, which `count` would tally as content.
    const isEmpty = React.Children.toArray(children).length === 0;
    const isEmptyWithCustomContent = isEmpty && Boolean(emptyContent);
    const isEmptyWithLegacyState =
        isEmpty && !emptyContent && Boolean(emptyTitle || emptyDescription || emptyIcon);

    let contentClassName = 'flex flex-col gap-4 p-4';
    if (isEmptyWithCustomContent) {
        // `flex-1`, not `min-h-full`: a percentage height only resolves against a parent with
        // an explicit height, and the scroll div's height comes from flex resolution rather
        // than a set value, so the percentage silently fails to resolve and the content
        // collapses to the top. `flex-1` instead asks the (now flex) scroll div's own layout
        // for a real height, which `mt-auto` below can then push against.
        contentClassName = 'flex flex-1 flex-col p-4';
    } else if (isEmptyWithLegacyState) {
        contentClassName =
            'flex min-h-full flex-col items-center justify-center gap-2 p-6 text-center';
    }

    return (
        <div className={`relative flex min-h-0 flex-1 flex-col ${className}`}>
            <div ref={scrollRef} className="flex min-h-0 flex-1 flex-col overflow-y-auto">
                <div
                    ref={contentRef}
                    role="log"
                    aria-live="polite"
                    aria-label={ariaLabel}
                    className={contentClassName}
                >
                    {isEmptyWithCustomContent ? (
                        // `margin-top: auto` consumes the free space the scroll div's flex
                        // layout gives this (now `flex-1`) content box, pinning the block to
                        // the bottom. `flex-direction: column-reverse` was the mock's own
                        // approach, but it inverts scroll geometry that `useStickToBottom` and
                        // the jump-to-latest control both rely on — this gets the same bottom
                        // anchoring without touching either.
                        <div className="mt-auto text-left">{emptyContent}</div>
                    ) : isEmptyWithLegacyState ? (
                        <>
                            {emptyIcon ? (
                                <span className="text-[var(--malachite-text-dimmed)]">
                                    {emptyIcon}
                                </span>
                            ) : null}
                            {emptyTitle ? (
                                <Typography.SecondaryText fontWeight="bold">
                                    {emptyTitle}
                                </Typography.SecondaryText>
                            ) : null}
                            {emptyDescription ? (
                                <Typography.SecondaryText size="small">
                                    {emptyDescription}
                                </Typography.SecondaryText>
                            ) : null}
                        </>
                    ) : (
                        children
                    )}
                </div>
            </div>
            {!pinned && (
                // Pinned to the side rather than centred over the newest message, which is
                // the thing a reader scrolled back to see. `-translate-y-1/2` is measured
                // working inside a shadow root (see the CardStrip arrow).
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <Button
                        variant="outline"
                        size="small"
                        iconOnly
                        radius="xl"
                        // Inline, because no utility class can reach it: Tailwind's utilities
                        // sit in `@layer utilities` while `.btn-outline`'s transparent
                        // background is an unlayered rule, and unlayered wins. Without this
                        // the transcript reads straight through the button.
                        style={{
                            backgroundColor: 'var(--malachite-card-background)',
                            boxShadow: 'var(--malachite-shadow-md)',
                        }}
                        icon={<CaretDownIcon size={14} />}
                        tooltip="Jump to latest"
                        aria-label="Jump to latest"
                        onClick={scrollToBottom}
                    />
                </div>
            )}
        </div>
    );
};

export default ChatThread;
export { ChatThread };
